import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  createVrijeDag,
  deleteVrijeDag,
  fetchVakanties,
  restoreVrijeDag,
  saveVakantieaanpassing,
  saveVakantieregio,
  updateVrijeDag,
} from "#/modules/les.planning/api.vakanties.server";
import { MAX_NAAM_LENGTH, type VakantiesOverzicht } from "#/modules/les.planning/domain/vakanties";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { Vakantieregio } from "#/schema.nswag";

const regioSchema = z.object({ vakantieregio: apiEnum(Vakantieregio) });
export type VakantieregioInput = z.infer<typeof regioSchema>;

const aanpassingSchema = z.object({
  naam: z.string().min(1),
  start: z.iso.date(),
  eind: z.iso.date(),
});
export type VakantieaanpassingInput = z.infer<typeof aanpassingSchema>;

const vrijeDagSchema = z.object({
  naam: z.string().trim().min(1).max(MAX_NAAM_LENGTH),
  start: z.iso.date(),
  eind: z.iso.date(),
});
export type VrijeDagInput = z.infer<typeof vrijeDagSchema>;

const updateVrijeDagSchema = z.object({ id: z.string().min(1), vrijeDag: vrijeDagSchema });
export type UpdateVrijeDagInput = z.infer<typeof updateVrijeDagSchema>;

const idSchema = z.object({ id: z.string().min(1) });
export type VrijeDagIdInput = z.infer<typeof idSchema>;

export const getVakanties = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async (): Promise<VakantiesOverzicht> => {
    const result = await fetchVakanties();
    if (Result.isError(result)) throw result.error;
    const { vakantieregio, vakanties, feestdagen, vrijeDagen } = result.value;
    return { vakantieregio: vakantieregio ?? null, vakanties, feestdagen, vrijeDagen };
  });

export const saveVakantieregioFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: VakantieregioInput) => regioSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await saveVakantieregio(data);
    if (Result.isError(result)) return apiFailure(result.error);
  });

export const saveVakantieaanpassingFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: VakantieaanpassingInput) => aanpassingSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await saveVakantieaanpassing(data);
    if (Result.isError(result)) return apiFailure(result.error);
  });

export const createVrijeDagFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: VrijeDagInput) => vrijeDagSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await createVrijeDag(data);
    if (Result.isError(result)) return apiFailure(result.error);
    return { id: result.value.id };
  });

export const updateVrijeDagFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: UpdateVrijeDagInput) => updateVrijeDagSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await updateVrijeDag(data.id, data.vrijeDag);
    if (Result.isError(result)) return apiFailure(result.error);
  });

export const deleteVrijeDagFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: VrijeDagIdInput) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await deleteVrijeDag(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });

export const restoreVrijeDagFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: VrijeDagIdInput) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await restoreVrijeDag(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });
